import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings, telHref } from "@/context/SettingsContext";

const links = [
  { id: "hero", label: "Home", testid: "nav-link-home" },
  { id: "order", label: "Place Order", testid: "nav-link-order" },
  { id: "subscribe", label: "Standing Orders", testid: "nav-link-subscribe" },
  { id: "track", label: "Track Order", testid: "nav-link-track" },
];
const ROUTES = { order: "/order", subscribe: "/subscribe", track: "/track" };

export const Navbar = ({ solid = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolledY, setScrolledY] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useSettings();
  const scrolled = solid || scrolledY;

  useEffect(() => {
    const onScroll = () => setScrolledY(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (ROUTES[id]) return navigate(ROUTES[id]);
    if (id === "hero") {
      if (location.pathname !== "/") return navigate("/");
      return window.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (location.pathname !== "/") return navigate(`/#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [location]);

  const phone = company?.phone || "";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "cw-glass shadow-lg shadow-sky-900/5 border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <button data-testid="nav-logo-brand" onClick={() => go("hero")} className="flex items-center gap-2.5 group">
          <div className="relative w-12 h-12 lg:w-14 lg:h-14 rounded-2xl overflow-hidden shadow-lg shadow-sky-900/20 ring-2 ring-white/40">
            <img src="/brand/logo.png" alt="Cool Waves Water logo" className="w-full h-full object-cover" data-testid="nav-logo-img" />
          </div>
          <div className="text-left leading-none">
            <div className={`font-display font-extrabold text-lg tracking-tight ${scrolled ? "text-slate-900" : "text-white"}`}>Cool Waves</div>
            <div className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${scrolled ? "text-sky-600" : "text-sky-200"}`}>Water · Jamaica</div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                scrolled ? "text-slate-600 hover:text-sky-700 hover:bg-sky-50" : "text-sky-50/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {phone && (
            <a href={telHref(phone)} data-testid="nav-phone" className={`flex items-center gap-2 text-sm font-semibold ${scrolled ? "text-slate-700" : "text-white"}`}>
              <Phone className="w-4 h-4" /> {phone}
            </a>
          )}
          {/* <Button data-testid="nav-btn-admin-portal" onClick={() => navigate("/admin")} className="rounded-full bg-sky-500 hover:bg-sky-400 text-white font-semibold shadow-lg shadow-sky-500/30">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Admin Portal
          </Button> */}
        </div>

        <button className={`lg:hidden p-2 rounded-xl ${scrolled ? "text-slate-800" : "text-white"}`} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden cw-glass border-t border-slate-200 px-5 py-4 space-y-1">
          {links.map((l) => (
            <button key={l.id} data-testid={`${l.testid}-mobile`} onClick={() => go(l.id)} className="block w-full text-left px-4 py-3 rounded-xl text-slate-700 font-semibold hover:bg-sky-50">
              {l.label}
            </button>
          ))}
          {phone && (
            <a href={telHref(phone)} className="block px-4 py-3 text-slate-700 font-semibold"><Phone className="w-4 h-4 inline mr-2" />{phone}</a>
          )}
          {/*<Button onClick={() => navigate("/admin")} className="w-full rounded-xl bg-sky-600 text-white font-semibold mt-2">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Login
          </Button>*/}
        </div>
      )}
    </header>
  );
};
